<template>
  <v-app class="price-checker-app">
    <div class="price-checker-container">
      <!-- Left Section (60% width) - Banner Area -->
      <div class="left-section">
        <!-- Banner Section (70% height) -->
        <div class="banner-section">
          <v-overlay v-if="loading" absolute>
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </v-overlay>

          <!-- Dynamic Banner Content -->
          <div class="banner-content">
            <div class="banner-logo">
              <v-icon size="80" color="white">fal fa-search-dollar</v-icon>
            </div>
            <h1 class="banner-title">{{ $t("price_checker") }}</h1>
            <p class="banner-subtitle">
              {{ $t("scan_or_search_products") }}
            </p>

            <!-- Promotional content or store info -->
            <div class="banner-info">
              <div class="store-info">
                <h3>{{ storeName || "Welcome to Our Store" }}</h3>
                <p>
                  {{
                    storeDescription ||
                    "Find product prices and information instantly"
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Search Input Section (30% height) -->
        <div class="search-section">
          <div class="search-container">
            <h3 class="search-title">{{ $t("search_products") }}</h3>
            <div class="search-form">
              <div class="search-input-group">
                <v-text-field
                  v-model="searchQuery"
                  :placeholder="$t('enter_barcode_or_name')"
                  outlined
                  dense
                  hide-details
                  class="search-input"
                  @keyup.enter="searchProduct"
                  @input="onSearchInput"
                  clearable
                >
                  <template v-slot:prepend-inner>
                    <v-icon>mdi-magnify</v-icon>
                  </template>
                </v-text-field>
                <v-btn
                  color="primary"
                  large
                  @click="searchProduct"
                  :loading="loading"
                  class="search-btn"
                >
                  <v-icon left>fal fa-search</v-icon>
                  {{ $t("search") }}
                </v-btn>
              </div>

              <!-- Barcode Scanner Button -->
              <div class="barcode-section">
                <v-btn
                  outlined
                  color="primary"
                  @click="toggleBarcodeScanner"
                  class="barcode-btn"
                >
                  <v-icon left>fal fa-barcode-scan</v-icon>
                  {{ $t("scan_barcode") }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Section (40% width) - Price Checker -->
      <div class="right-section">
        <div class="price-checker-content">
          <!-- Header -->
          <div class="checker-header">
            <h2>{{ $t("product_information") }}</h2>
            <v-chip v-if="product" color="success" small>
              {{ $t("found") }}
            </v-chip>
          </div>

          <!-- Product Display Area -->
          <div class="product-display">
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <v-progress-circular
                indeterminate
                size="64"
                color="primary"
              ></v-progress-circular>
              <p>{{ $t("searching_for_product") }}</p>
            </div>

            <!-- Product Found -->
            <div v-else-if="product" class="product-card improved-card">
              <div class="product-image-section">
                <img
                  :src="IMAGES(product.img)"
                  :alt="product.name"
                  class="product-image"
                />
              </div>

              <div class="product-details">
                <div class="product-header">
                  <h3 class="product-name">{{ product.name }}</h3>
                  <div class="product-price">
                    <span class="currency">{{
                      product.currency?.code || "THB"
                    }}</span>
                    <span class="amount">{{
                      formatPrice(product.price_sale || product.price)
                    }}</span>
                  </div>
                  <div
                    v-if="
                      product.price_buy &&
                      product.price_sale &&
                      product.price_buy !== product.price_sale
                    "
                    class="product-buy-price"
                  >
                    <span class="buy-label">{{ $t("buy_price") }}:</span>
                    <span class="buy-amount">{{
                      formatPrice(product.price_buy)
                    }}</span>
                  </div>
                </div>

                <div class="product-info-grid">
                  <div class="info-item">
                    <span class="info-label">{{ $t("barcode") }}:</span>
                    <span class="info-value">{{ product.code || "N/A" }}</span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">{{ $t("category") }}:</span>
                    <span class="info-value">
                      {{
                        product.category?.la_name ||
                        product.category?.en_name ||
                        "N/A"
                      }}
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">{{ $t("stock") }}:</span>
                    <span
                      class="info-value"
                      :class="getStockClass(product.stock)"
                    >
                      {{ product.stock || 0 }}
                      <span
                        v-if="product.stock <= (product.notify_stock || 10)"
                        class="stock-warning"
                      >
                        ({{
                          product.stock === 0
                            ? $t("out_of_stock")
                            : $t("low_stock")
                        }})
                      </span>
                    </span>
                  </div>

                  <div class="info-item">
                    <span class="info-label">{{ $t("unit") }}:</span>
                    <span class="info-value">
                      {{ product.size?.name || "N/A" }}
                      <span v-if="product.size?.unit"
                        >({{ product.size.unit }})</span
                      >
                    </span>
                  </div>

                  <div v-if="product.vendor" class="info-item">
                    <span class="info-label">{{ $t("supplier") }}:</span>
                    <span class="info-value">{{ product.vendor?.name }}</span>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="product-actions">
                  <v-btn
                    outlined
                    color="primary"
                    @click="copyProductInfo"
                    class="action-btn"
                  >
                    <v-icon left>fal fa-copy</v-icon>
                    {{ $t("copy_info") }}
                  </v-btn>

                  <v-btn
                    outlined
                    color="secondary"
                    @click="clearSearch"
                    class="action-btn"
                  >
                    <v-icon left>fal fa-sync</v-icon>
                    {{ $t("new_search") }}
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- No Product Found -->
            <div v-else-if="searched && !product" class="no-product">
              <v-icon size="80" color="grey lighten-2">fal fa-box-open</v-icon>
              <h3>{{ $t("product_not_found") }}</h3>
              <p>{{ $t("product_not_found") }}</p>
              <p>{{ $t("please_enter_search_term") }}</p>
            </div>

            <!-- Initial State -->
            <div v-else class="initial-state">
              <v-icon size="80" color="primary lighten-2"
                >fal fa-search-dollar</v-icon
              >
              <h3>{{ $t("ready_to_search") }}</h3>
              <p>{{ $t("enter_barcode_to_start") }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
import { myMixin } from "@/mixin/main";
import { toast } from "@/controllers/alert";

export default {
  layout: "blank",
  mixins: [myMixin],
  data() {
    return {
      searchQuery: "",
      product: null,
      loading: false,
      searched: false,
      storeName: "",
      storeDescription: "",
    };
  },

  async mounted() {
    await this.loadStoreInfo();
  },

  methods: {
    async loadStoreInfo() {
      try {
        // Load store information for banner
        const store = this.$store.state.auth.store;
        if (store) {
          this.storeName = store.name;
          this.storeDescription =
            store.desc || "Find product prices and information instantly";
        }
      } catch (error) {
        console.error("Error loading store info:", error);
      }
    },

    async searchProduct() {
      if (!this.searchQuery.trim()) {
        toast(this, "please_enter_search_term", "warning");
        return;
      }

      this.loading = true;
      this.searched = true;

      try {
        const { data } = await this.$http.get(
          `product_search?q=${this.searchQuery.trim()}`
        );

        if (data.status) {
          this.product = data.data;
          toast(this, "product_found_successfully", "success");
        } else {
          this.product = null;
          toast(this, "product_not_found", "warning");
        }
      } catch (error) {
        console.error("Error searching product:", error);
        this.product = null;
        toast(this, "search_error", "error");
      } finally {
        this.loading = false;
      }
    },

    onSearchInput() {
      // Clear product when user starts typing new search
      if (this.product) {
        this.product = null;
        this.searched = false;
      }
    },

    toggleBarcodeScanner() {
      // Placeholder for barcode scanner functionality
      toast(this, "barcode_scanner_coming_soon", "info");
    },

    copyProductInfo() {
      if (!this.product) return;

      const productInfo = `
Product: ${this.product.name}
Price: ${this.formatPrice(this.product.price_sale || this.product.price)}
Barcode: ${this.product.code || "N/A"}
Category: ${this.product.category?.name || "N/A"}
Stock: ${this.product.stock || 0}
      `.trim();

      navigator.clipboard
        .writeText(productInfo)
        .then(() => {
          toast(this, "product_info_copied", "success");
        })
        .catch(() => {
          toast(this, "failed_to_copy", "error");
        });
    },

    clearSearch() {
      this.searchQuery = "";
      this.product = null;
      this.searched = false;
    },

    formatPrice(price) {
      if (!price) return "0.00";
      return parseFloat(price).toLocaleString(undefined, {
        minimumFractionDigits: 2,
      });
    },

    getStockClass(stock) {
      if (!stock || stock <= 0) return "stock-out";
      if (stock <= 10) return "stock-low";
      return "stock-ok";
    },
  },
};
</script>

<style scoped>
.price-checker-app {
  height: 100vh;
  overflow: hidden;
}

.price-checker-container {
  display: flex;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Left Section (60% width) */
.left-section {
  width: 60%;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

/* Banner Section (70% height) */
.banner-section {
  height: 70%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.9) 0%,
    rgba(118, 75, 162, 0.9) 100%
  );
}

.banner-content {
  text-align: center;
  color: white;
  padding: 2rem;
}

.banner-logo {
  margin-bottom: 1rem;
}

.banner-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.banner-subtitle {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.banner-info {
  margin-top: 2rem;
}

.store-info h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.store-info p {
  font-size: 1rem;
  opacity: 0.8;
}

/* Search Section (30% height) */
.search-section {
  height: 30%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 1rem;
}

.search-container {
  width: 100%;
  max-width: 500px;
}

.search-title {
  text-align: center;
  margin-bottom: 1rem;
  color: #333;
  font-size: 1.2rem;
}

.search-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.search-input-group {
  display: flex;
  gap: 0.5rem;
}

.search-input {
  flex: 1;
}

.search-btn {
  min-width: 120px;
}

.barcode-section {
  display: flex;
  justify-content: center;
}

.barcode-btn {
  min-width: 150px;
}

/* Right Section (40% width) */
.right-section {
  width: 40%;
  background: white;
  display: flex;
  flex-direction: column;
}

.price-checker-content {
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
}

.checker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
}

.checker-header h2 {
  color: #333;
  font-size: 1.8rem;
  font-weight: 700;
}

.product-display {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
}

.loading-state p {
  color: #666;
  font-size: 1.1rem;
}

/* Product Card */
.product-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.improved-card {
  animation: fadeIn 0.5s;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
  border-radius: 16px;
  margin-top: 16px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.product-buy-price {
  font-size: 1rem;
  color: #888;
  margin-top: 0.5rem;
}

.stock-warning {
  color: #ff9800;
  font-size: 0.9em;
  margin-left: 4px;
}

.product-image-section {
  height: 200px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e9ecef;
}

.product-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.product-details {
  flex: 1;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
}

.product-header {
  margin-bottom: 1.5rem;
}

.product-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.5rem;
  line-height: 1.3;
}

.product-price {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.currency {
  font-size: 1rem;
  color: #666;
}

.amount {
  font-size: 2rem;
  font-weight: 800;
  color: #667eea;
}

.product-info-grid {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.info-value {
  font-size: 1rem;
  color: #333;
  font-weight: 600;
}

.stock-out {
  color: #dc3545;
}

.stock-low {
  color: #ffc107;
}

.stock-ok {
  color: #28a745;
}

.product-actions {
  display: flex;
  gap: 1rem;
  margin-top: auto;
}

.action-btn {
  flex: 1;
}

/* No Product State */
.no-product {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  gap: 1rem;
}

.no-product h3 {
  color: #666;
  font-size: 1.3rem;
}

.no-product p {
  color: #999;
  font-size: 1rem;
}

/* Initial State */
.initial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  gap: 1rem;
}

.initial-state h3 {
  color: #667eea;
  font-size: 1.3rem;
}

.initial-state p {
  color: #666;
  font-size: 1rem;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .left-section {
    width: 55%;
  }

  .right-section {
    width: 45%;
  }

  .banner-title {
    font-size: 2.5rem;
  }
}

@media (max-width: 768px) {
  .price-checker-container {
    flex-direction: column;
  }

  .left-section {
    width: 100%;
    height: 50%;
  }

  .right-section {
    width: 100%;
    height: 50%;
  }

  .banner-section {
    height: 60%;
  }

  .search-section {
    height: 40%;
  }

  .banner-title {
    font-size: 2rem;
  }

  .product-info-grid {
    grid-template-columns: 1fr;
  }

  .product-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .price-checker-content {
    padding: 1rem;
  }

  .banner-title {
    font-size: 1.5rem;
  }

  .banner-subtitle {
    font-size: 1rem;
  }

  .search-input-group {
    flex-direction: column;
  }

  .search-btn {
    min-width: auto;
  }
}
</style>
